import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import {
  useMyPendingSignatures,
  useSignTimePeriod,
  useDisputeSignature,
} from '@/hooks/useTimeTrackingAudit';
import { MonthlyTimeSummary, formatMinutesToTime } from '@/types/timeTracking';
import {
  PenLine,
  Calendar,
  Clock,
  CheckCircle,
  AlertTriangle,
  FileText,
  Info,
  Loader2,
  XCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';

export function TimeSignatureCard() {
  const { toast } = useToast();
  const [selectedPeriod, setSelectedPeriod] = useState<MonthlyTimeSummary | null>(null);
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [notes, setNotes] = useState('');
  const [disputeNotes, setDisputeNotes] = useState('');

  const { data: pendingSignatures, isLoading } = useMyPendingSignatures();
  const signMutation = useSignTimePeriod();
  const disputeMutation = useDisputeSignature();

  const handleSign = async () => {
    if (!selectedPeriod || !acceptedTerms) return;

    try {
      await signMutation.mutateAsync({
        periodStart: selectedPeriod.month_start,
        periodEnd: selectedPeriod.month_end,
        employeeNotes: notes || undefined,
      });

      toast({
        title: 'Horas firmadas',
        description: 'Tu registro de horas ha sido firmado correctamente',
      });

      setSignDialogOpen(false);
      setSelectedPeriod(null);
      setAcceptedTerms(false);
      setNotes('');
    } catch (error: any) {
      toast({
        title: 'Error al firmar',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleDispute = async () => {
    if (!selectedPeriod?.signature || !disputeNotes.trim()) return;

    try {
      await disputeMutation.mutateAsync({
        signatureId: selectedPeriod.signature.id,
        notes: disputeNotes,
      });

      toast({
        title: 'Discrepancia registrada',
        description: 'Tu solicitud de revisión ha sido registrada',
      });

      setDisputeDialogOpen(false);
      setSelectedPeriod(null);
      setDisputeNotes('');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const openSignDialog = (period: MonthlyTimeSummary) => {
    setSelectedPeriod(period);
    setSignDialogOpen(true);
  };

  const openDisputeDialog = (period: MonthlyTimeSummary) => {
    setSelectedPeriod(period);
    setDisputeDialogOpen(true);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (!pendingSignatures?.length) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">
            <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
            <p className="font-medium">Todas tus horas están firmadas</p>
            <p className="text-sm mt-1">No tienes períodos pendientes de firma</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PenLine className="w-5 h-5" />
            Firma de Horas Trabajadas
          </CardTitle>
          <CardDescription>
            Conforme al Art. 34.9 del Estatuto de los Trabajadores (RDL 8/2019)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <Info className="w-4 h-4" />
            <AlertTitle>Firma requerida</AlertTitle>
            <AlertDescription>
              Tienes {pendingSignatures.length} período(s) pendiente(s) de firmar.
              La firma confirma que las horas registradas son correctas.
            </AlertDescription>
          </Alert>

          {pendingSignatures.map((period) => (
            <Card key={`${period.month_start}_${period.month_end}`} className="border-dashed">
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">
                        {format(new Date(period.month_start), 'MMMM yyyy', { locale: es })}
                      </span>
                      {period.signature?.status === 'pending' && (
                        <Badge variant="secondary">Pendiente revisión</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {period.total_hours?.toFixed(1) || 0}h trabajadas
                      </span>
                      <span>{period.days_worked || 0} días</span>
                      <span>{(period.total_break_hours || 0).toFixed(1)}h pausas</span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {!period.signature ? (
                      <Button onClick={() => openSignDialog(period)}>
                        <PenLine className="w-4 h-4 mr-2" />
                        Firmar
                      </Button>
                    ) : period.signature.status === 'pending' ? (
                      <>
                        <Button variant="outline" onClick={() => openDisputeDialog(period)}>
                          <AlertTriangle className="w-4 h-4 mr-2" />
                          Disputar
                        </Button>
                        <Button onClick={() => openSignDialog(period)}>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Confirmar
                        </Button>
                      </>
                    ) : null}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </CardContent>
      </Card>

      {/* Sign Dialog */}
      <Dialog open={signDialogOpen} onOpenChange={setSignDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenLine className="w-5 h-5" />
              Firmar Horas Trabajadas
            </DialogTitle>
            <DialogDescription>
              {selectedPeriod && format(new Date(selectedPeriod.month_start), 'MMMM yyyy', { locale: es })}
            </DialogDescription>
          </DialogHeader>

          {selectedPeriod && (
            <div className="space-y-4">
              <Card className="bg-muted/50">
                <CardContent className="pt-6">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold">
                        {selectedPeriod.total_hours?.toFixed(1) || 0}h
                      </p>
                      <p className="text-sm text-muted-foreground">Horas Trabajadas</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{selectedPeriod.days_worked || 0}</p>
                      <p className="text-sm text-muted-foreground">Días Trabajados</p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold">
                        {(selectedPeriod.total_break_hours || 0).toFixed(1)}h
                      </p>
                      <p className="text-sm text-muted-foreground">Pausas</p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold">
                        {(selectedPeriod.avg_daily_hours || 0).toFixed(1)}h
                      </p>
                      <p className="text-sm text-muted-foreground">Media Diaria</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-2">
                <Label>Observaciones (opcional)</Label>
                <Textarea
                  placeholder="Añade cualquier observación sobre este período..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="flex items-start space-x-3 p-4 rounded-lg border bg-muted/30">
                <Checkbox
                  id="accept-terms"
                  checked={acceptedTerms}
                  onCheckedChange={(checked) => setAcceptedTerms(checked as boolean)}
                />
                <label
                  htmlFor="accept-terms"
                  className="text-sm leading-relaxed cursor-pointer"
                >
                  Confirmo que las horas registradas corresponden fielmente a mi jornada laboral
                  durante el período indicado, conforme al Art. 34.9 del Estatuto de los Trabajadores.
                </label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSignDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSign}
              disabled={!acceptedTerms || signMutation.isPending}
            >
              {signMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Firmar y Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispute Dialog */}
      <Dialog open={disputeDialogOpen} onOpenChange={setDisputeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="w-5 h-5" />
              Reportar Discrepancia
            </DialogTitle>
            <DialogDescription>
              Indica los motivos por los que no estás de acuerdo con las horas registradas
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Alert variant="destructive">
              <AlertTriangle className="w-4 h-4" />
              <AlertTitle>Atención</AlertTitle>
              <AlertDescription>
                Esta acción generará una solicitud de revisión que será atendida por RRHH.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label>Describe la discrepancia *</Label>
              <Textarea
                placeholder="Explica detalladamente qué horas no coinciden y cuál debería ser el valor correcto..."
                value={disputeNotes}
                onChange={(e) => setDisputeNotes(e.target.value)}
                rows={4}
                required
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDisputeDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDispute}
              disabled={!disputeNotes.trim() || disputeMutation.isPending}
            >
              {disputeMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              Enviar Discrepancia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default TimeSignatureCard;
